import { ArrowLeftIcon, Download } from "@/components/icons";
import { Button, Skeleton, toast } from "@/components/kit";
import { useShopSettingsQuery } from "@/modules/chat-tools/presentation/api/queries";
import { downloadElementAsPng } from "@/modules/chat-tools/presentation/lib/download-label-image";
import { buildCustomJobSummaryCardViewModel } from "@/modules/custom-jobs/domain/build-custom-job-summary-card";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useCustomJobQuery } from "../api/queries";
import { CustomJobSummaryCard } from "../ui/CustomJobSummaryCard";

export function CustomJobSummaryImagePage() {
  const { id } = useParams({ from: "/app/custom-jobs/$id/summary-image" });
  const nav = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const jobQuery = useCustomJobQuery(id);
  const settingsQuery = useShopSettingsQuery();
  const job = jobQuery.data;
  const settings = settingsQuery.data;

  const isLoading = jobQuery.isLoading || settingsQuery.isLoading;

  async function handleDownload() {
    const element = cardRef.current;
    if (!element || !job) return;

    setIsDownloading(true);
    try {
      const safeName = job.jobNumber.replace(/[^\w-]+/g, "-");
      await downloadElementAsPng(element, `${safeName}-custom-job-summary.png`);
      toast.success("ດາວໂຫລດຮູບສະຫຼຸບແລ້ວ");
    } catch (e) {
      toast.error("ດາວໂຫລດບໍ່ສຳເລັດ", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setIsDownloading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <Skeleton className="h-128 w-full max-w-xs rounded-3xl" />
      </div>
    );
  }

  if (!job || !settings) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background">
        <p className="text-muted-foreground">ບໍ່ພົບງານສັ່ງເຮັດ</p>
        <Button
          variant="outline"
          className="no-print rounded-full"
          onClick={() => nav({ to: "/app/custom-jobs" })}
        >
          ກັບໄປລາຍການ
        </Button>
      </div>
    );
  }

  const summary = buildCustomJobSummaryCardViewModel(job, settings);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background text-foreground">
      <div className="no-print flex items-center justify-between gap-3 border-border border-b bg-background px-4 py-3">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => nav({ to: "/app/custom-jobs/$id", params: { id } })}
        >
          <ArrowLeftIcon data-icon="inline-start" />
          ກັບຄືນ
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            isLoading={isDownloading}
            onClick={handleDownload}
          >
            <Download data-icon="inline-start" />
            ດາວໂຫລດຮູບ
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => window.print()}
          >
            ພິມ
          </Button>
        </div>
      </div>

      <div className="flex flex-1 items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6 print:p-0">
        <CustomJobSummaryCard
          ref={cardRef}
          id="custom-job-summary-print"
          summary={summary}
        />
      </div>
    </div>
  );
}
